/**
 * AnimationFixToolTip - A wrapper around Recharts Tooltip that fixes the jarring fly-in animation
 *
 * This component prevents the tooltip from flying in from the upper left corner on first hover.
 * Instead, it appears instantly on first hover, then animates smoothly as it moves between data points.
 *
 * **Note:** This is a hack that manipulates Recharts' animation state. If it causes any issues in the
 * future, you can revert to using the standard `<Tooltip />` component and optionally disable animation
 * entirely via the `isAnimationActive={false}` prop.
 *
 * @example Basic usage with custom content
 * ```tsx
 * import AnimationFixToolTip from '../base/AnimationFixToolTip';
 *
 * <ComposedChart data={chartData}>
 *   <AnimationFixToolTip
 *     content={<CustomTooltipContent />}
 *     cursor={{ fill: 'transparent' }}
 *   />
 * </ComposedChart>
 * ```
 *
 * @example With formatter and labelFormatter
 * ```tsx
 * <AreaChart data={chartData}>
 *   <AnimationFixToolTip
 *     content={<ChartTooltipContent />}
 *     formatter={(value: number) => `$${value.toLocaleString()}`}
 *     labelFormatter={(date: Date) => date.toLocaleDateString()}
 *     cursor={{ className: 'stroke-utility-brand-600' }}
 *   />
 * </AreaChart>
 * ```
 *
 * @example Simple usage with default content
 * ```tsx
 * <BarChart data={chartData}>
 *   <AnimationFixToolTip cursor={{ fill: 'transparent' }} />
 * </BarChart>
 * ```
 *
 * @example Alternative: Disable animation entirely with standard Tooltip
 * ```tsx
 * import { Tooltip } from 'recharts';
 *
 * <BarChart data={chartData}>
 *   <Tooltip isAnimationActive={false} />
 * </BarChart>
 * ```
 */

import * as React from 'react';
import { Tooltip } from 'recharts';

import type { TooltipProps } from 'recharts';

// Recharts ContentType for tooltips
type ContentType<TValue extends number | string, TName extends number | string> =
  | React.ReactElement
  | ((props: TooltipProps<TValue, TName>) => React.ReactNode);

interface IAnimationFixToolTipProps<TValue extends number | string, TName extends number | string>
  extends Omit<TooltipProps<TValue, TName>, 'isAnimationActive' | 'content'> {
  /**
   * Custom content component for the tooltip
   */
  content?: ContentType<TValue, TName>;
}

/**
 * Inner component that wraps the tooltip content and tracks active state
 */
const TooltipContentTracker = ({
  content,
  onActiveChange,
  ...contentProps
}: {
  content: React.ReactElement | React.ComponentType<Record<string, unknown>>;
  onActiveChange: (isActive: boolean) => void;
  active?: boolean;
  [key: string]: unknown;
}) => {
  const isActive = contentProps.active ?? false;
  const prevActiveRef = React.useRef(isActive);

  React.useEffect(() => {
    if (isActive !== prevActiveRef.current) {
      prevActiveRef.current = isActive;
      onActiveChange(isActive);
    }
  }, [isActive, onActiveChange]);

  // If content is a function, call it with props
  if (typeof content === 'function') {
    const Content = content;
    return <Content {...contentProps} />;
  }

  // If content is a React element, clone it with the props
  return React.isValidElement(content) ? React.cloneElement(content, contentProps) : null;
};

const AnimationFixToolTip = <TValue extends number | string = number, TName extends number | string = string>({
  content,
  ...props
}: IAnimationFixToolTipProps<TValue, TName>) => {
  const [isAnimationEnabled, setIsAnimationEnabled] = React.useState(false);
  const wasActiveRef = React.useRef(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Track when tooltip becomes active/inactive
  const handleActiveStateChange = React.useCallback((isActive: boolean) => {
    if (isActive && !wasActiveRef.current) {
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Tooltip just became active - disable animation for initial appearance
      setIsAnimationEnabled(false);
      wasActiveRef.current = true;
      // Enable animation after a short delay so subsequent movements are animated
      timeoutRef.current = setTimeout(() => {
        setIsAnimationEnabled(true);
        timeoutRef.current = null;
      }, 50);
    } else if (!isActive && wasActiveRef.current) {
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Tooltip became inactive - prepare for next activation
      wasActiveRef.current = false;
      setIsAnimationEnabled(false);
    }
  }, []);

  // Create a wrapper component to track active state changes
  const wrappedContent = React.useMemo(() => {
    if (!content) {
      return undefined;
    }

    return (contentProps: unknown) => (
      <TooltipContentTracker content={content} onActiveChange={handleActiveStateChange} {...(contentProps as object)} />
    );
  }, [content, handleActiveStateChange]);

  return <Tooltip {...props} isAnimationActive={isAnimationEnabled} content={wrappedContent} />;
};

export default AnimationFixToolTip;
